import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  CSidebar, CSidebarBrand, CSidebarNav, CSidebarToggler,
  CNavItem, CNavTitle, CNavGroup,
  CHeader, CHeaderNav, CHeaderToggler, CHeaderBrand,
  CContainer, CBreadcrumb, CBreadcrumbItem,
  CDropdown, CDropdownToggle, CDropdownMenu, CDropdownItem,
  CAvatar,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import {
  cilSpeedometer, cilPeople, cilUserPlus,
  cilTask, cilPlus, cilShieldAlt,
  cilSettings, cilMenu, cilAccountLogout, cilUser,
  cilNotes, cilContact, cilCog, cilChartLine, cilSend, cilGift, cilChatBubble,
} from '@coreui/icons';
import { getUser, logout, isAdmin, isFinance } from '../services/auth';
import NotificationBell from './NotificationBell';
import NotificationInbox from './NotificationInbox';
import HelpDeskIcon from './HelpDeskIcon';
import './Layout.scss';

const ROUTES = [
  { path: '/dashboard',       label: 'Dashboard' },
  { path: '/customers',       label: 'Customer List' },
  { path: '/customers/new',   label: 'Add Customer' },
  { path: '/helpdesk',        label: 'Helpdesk' },
  { path: '/jobs',            label: 'Job List' },
  { path: '/jobs/new',        label: 'New Job' },
  { path: '/warranty',        label: 'Warranty List' },
  { path: '/warranty/new',    label: 'New Claim' },
  { path: '/finance/invoices', label: 'Invoices' },
  { path: '/finance/invoice/new', label: 'Generate Invoice' },
  { path: '/finance/payment/new', label: 'Record Payment' },
  { path: '/finance/payments', label: 'Payment History' },
  { path: '/admin/users',     label: 'Manage Users' },
  { path: '/admin/users/new', label: 'Add User' },
  { path: '/admin/config',       label: 'Configuration' },
  { path: '/admin/performance',  label: 'Technician Performance' },
  { path: '/marketing/send',     label: 'Marketing — Send WhatsApp' },
  { path: '/marketing/templates', label: 'Marketing — Templates' },
  { path: '/profile',         label: 'My Profile' },
];

// Groups and which path prefix they own
const GROUPS = ['customers', 'jobs', 'warranty', 'marketing', 'finance', 'admin', 'helpdesk'];

function useBreadcrumbs() {
  const { pathname } = useLocation();
  const base = process.env.PUBLIC_URL || '';
  const rel  = pathname.startsWith(base) ? pathname.slice(base.length) || '/' : pathname;
  const sorted = [...ROUTES].sort((a, b) => b.path.length - a.path.length);
  const match = sorted.find(r => rel === r.path || rel.startsWith(r.path + '/'));
  return match ? match.label : 'Dashboard';
}

function navLinkClass({ isActive }) {
  return `nav-link${isActive ? ' active' : ''}`;
}

// Derive which group is active from pathname
function activeGroup(pathname) {
  return GROUPS.find(g => pathname.includes(`/${g}`)) || null;
}

export default function Layout() {
  const navigate = useNavigate();
  const user = getUser();
  const { pathname } = useLocation();
  const [sidebarShow, setSidebarShow] = useState(true);
  const [openGroup, setOpenGroup] = useState(() => activeGroup(pathname));
  const currentPage = useBreadcrumbs();

  // When route changes (e.g. NavLink click), auto-expand the matching group
  useEffect(() => {
    const group = activeGroup(pathname);
    if (group) setOpenGroup(group);
  }, [pathname]);

  function toggleGroup(name) {
    setOpenGroup(prev => prev === name ? null : name);
  }

  function handleLogout() {
    logout();
    navigate('/login');
  }

  const initials = (user?.nama || user?.username || 'U')
    .split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="app d-flex">
      <CSidebar position="fixed" visible={sidebarShow} onVisibleChange={setSidebarShow} unfoldable={false}>
        <CSidebarBrand>
          <img
            src={`${process.env.PUBLIC_URL}/images/fmzlogo.png`}
            alt="FMZ"
            style={{ height: 36, width: 'auto' }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        </CSidebarBrand>

        <CSidebarNav>
          <CNavItem>
            <NavLink to="/dashboard" className={navLinkClass}>
              <CIcon icon={cilSpeedometer} customClassName="nav-icon" />
              Dashboard
            </NavLink>
          </CNavItem>

          <CNavTitle>Customers</CNavTitle>
          <CNavGroup
            visible={openGroup === 'customers'}
            onVisibleChange={() => toggleGroup('customers')}
            toggler={<><CIcon icon={cilPeople} customClassName="nav-icon" /> Customers</>}
          >
            <CNavItem>
              <NavLink to="/customers" end className={navLinkClass}>
                <CIcon icon={cilPeople} customClassName="nav-icon" />
                Customer List
              </NavLink>
            </CNavItem>
            <CNavItem>
              <NavLink to="/customers/new" className={navLinkClass}>
                <CIcon icon={cilUserPlus} customClassName="nav-icon" />
                Add Customer
              </NavLink>
            </CNavItem>
            <CNavItem>
              <NavLink to="/helpdesk" className={navLinkClass}>
                <CIcon icon={cilChatBubble} customClassName="nav-icon" />
                Helpdesk
              </NavLink>
            </CNavItem>
          </CNavGroup>

          <CNavTitle>Jobs</CNavTitle>
          <CNavGroup
            visible={openGroup === 'jobs'}
            onVisibleChange={() => toggleGroup('jobs')}
            toggler={<><CIcon icon={cilTask} customClassName="nav-icon" /> Jobs</>}
          >
            <CNavItem>
              <NavLink to="/jobs" end className={navLinkClass}>
                <CIcon icon={cilTask} customClassName="nav-icon" />
                Job List
              </NavLink>
            </CNavItem>
            <CNavItem>
              <NavLink to="/jobs/new" className={navLinkClass}>
                <CIcon icon={cilPlus} customClassName="nav-icon" />
                New Job
              </NavLink>
            </CNavItem>
          </CNavGroup>

          <CNavTitle>Warranty</CNavTitle>
          <CNavGroup
            visible={openGroup === 'warranty'}
            onVisibleChange={() => toggleGroup('warranty')}
            toggler={<><CIcon icon={cilShieldAlt} customClassName="nav-icon" /> Warranty</>}
          >
            <CNavItem>
              <NavLink to="/warranty" end className={navLinkClass}>
                <CIcon icon={cilShieldAlt} customClassName="nav-icon" />
                Warranty List
              </NavLink>
            </CNavItem>
            <CNavItem>
              <NavLink to="/warranty/new" className={navLinkClass}>
                <CIcon icon={cilPlus} customClassName="nav-icon" />
                New Claim
              </NavLink>
            </CNavItem>
          </CNavGroup>

          <CNavTitle>Marketing</CNavTitle>
          <CNavGroup
            visible={openGroup === 'marketing'}
            onVisibleChange={() => toggleGroup('marketing')}
            toggler={<><CIcon icon={cilSend} customClassName="nav-icon" /> Marketing</>}
          >
            <CNavItem>
              <NavLink to="/marketing/send" end className={navLinkClass}>
                <CIcon icon={cilSend} customClassName="nav-icon" />
                Send WhatsApp
              </NavLink>
            </CNavItem>
            <CNavItem>
              <NavLink to="/marketing/templates" className={navLinkClass}>
                <CIcon icon={cilNotes} customClassName="nav-icon" />
                Message templates
              </NavLink>
            </CNavItem>
          </CNavGroup>

          {/* Finance: admins + account staff (`isFinance` = isAdmin || isAccount) */}
          {isFinance() && (
            <>
              <CNavTitle>Finance / Account</CNavTitle>
              <CNavGroup
                visible={openGroup === 'finance'}
                onVisibleChange={() => toggleGroup('finance')}
                toggler={<><CIcon icon={cilNotes} customClassName="nav-icon" /> Finance / Account</>}
              >
                <CNavItem>
                  <NavLink to="/finance/invoices" end className={navLinkClass}>
                    <CIcon icon={cilNotes} customClassName="nav-icon" />
                    Invoices
                  </NavLink>
                </CNavItem>
                <CNavItem>
                  <NavLink to="/finance/invoice/new" className={navLinkClass}>
                    <CIcon icon={cilPlus} customClassName="nav-icon" />
                    Generate invoice
                  </NavLink>
                </CNavItem>
                <CNavItem>
                  <NavLink to="/finance/payment/new" className={navLinkClass}>
                    <CIcon icon={cilTask} customClassName="nav-icon" />
                    Record payment
                  </NavLink>
                </CNavItem>
                <CNavItem>
                  <NavLink to="/finance/payments" end className={navLinkClass}>
                    <CIcon icon={cilNotes} customClassName="nav-icon" />
                    Payment history
                  </NavLink>
                </CNavItem>
                <CNavItem>
                  <NavLink to="/finance/points-history" end className={navLinkClass}>
                    <CIcon icon={cilGift} customClassName="nav-icon" />
                    FMZ Points history
                  </NavLink>
                </CNavItem>
              </CNavGroup>
            </>
          )}

          {isAdmin() && (
            <>
              <CNavTitle>Admin</CNavTitle>
              <CNavGroup
                visible={openGroup === 'admin'}
                onVisibleChange={() => toggleGroup('admin')}
                toggler={<><CIcon icon={cilSettings} customClassName="nav-icon" /> Admin</>}
              >
                <CNavItem>
                  <NavLink to="/admin/users" className={navLinkClass}>
                    <CIcon icon={cilUser} customClassName="nav-icon" />
                    Manage Users
                  </NavLink>
                </CNavItem>
                <CNavItem>
                  <NavLink to="/admin/users/new" className={navLinkClass}>
                    <CIcon icon={cilPlus} customClassName="nav-icon" />
                    Add User
                  </NavLink>
                </CNavItem>
                <CNavItem>
                  <NavLink to="/admin/config" className={navLinkClass}>
                    <CIcon icon={cilCog} customClassName="nav-icon" />
                    Configuration
                  </NavLink>
                </CNavItem>
                <CNavItem>
                  <NavLink to="/admin/performance" className={navLinkClass}>
                    <CIcon icon={cilChartLine} customClassName="nav-icon" />
                    Performance
                  </NavLink>
                </CNavItem>
              </CNavGroup>
            </>
          )}
          <CNavTitle>Session</CNavTitle>
          <CNavItem>
            <NavLink to="/profile" className={navLinkClass}>
              <CIcon icon={cilContact} customClassName="nav-icon" />
              My Profile
            </NavLink>
          </CNavItem>
          <CNavItem>
            <span className="nav-link" onClick={handleLogout} style={{ cursor: 'pointer' }}>
              <CIcon icon={cilAccountLogout} customClassName="nav-icon" />
              Logout
            </span>
          </CNavItem>
        </CSidebarNav>

        <CSidebarToggler onClick={() => setSidebarShow(!sidebarShow)} />
      </CSidebar>

      <div
        className="wrapper d-flex flex-column min-vh-100 flex-grow-1"
        style={{ marginLeft: sidebarShow ? 256 : 0, transition: 'margin-left 0.3s' }}
      >
        <CHeader position="sticky" className="mb-0 px-3">
          <CHeaderToggler onClick={() => setSidebarShow(!sidebarShow)}>
            <CIcon icon={cilMenu} size="lg" />
          </CHeaderToggler>

          <CHeaderBrand className="mx-auto d-md-none">FMZ Repair</CHeaderBrand>

          <CHeaderNav className="ms-auto d-flex align-items-center gap-1">
            <HelpDeskIcon />
            <NotificationBell />
            <NotificationInbox />

            <CDropdown variant="nav-item" placement="bottom-end">
              <CDropdownToggle className="py-0 d-flex align-items-center gap-2" caret={false}>
                <CAvatar size="sm" style={{ background: '#8bc34a', color: '#fff', fontSize: 12, fontWeight: 500 }}>
                  {initials}
                </CAvatar>
                <span className="d-none d-md-inline" style={{ fontSize: 14 }}>
                  {user?.nama || user?.username}
                </span>
              </CDropdownToggle>
              <CDropdownMenu>
                <CDropdownItem onClick={() => navigate('/profile')} style={{ cursor: 'pointer', fontSize: 14 }}>
                  <CIcon icon={cilContact} className="me-2" />
                  My Profile
                </CDropdownItem>
                <CDropdownItem onClick={handleLogout} style={{ cursor: 'pointer', fontSize: 14 }}>
                  <CIcon icon={cilAccountLogout} className="me-2" />
                  Logout
                </CDropdownItem>
              </CDropdownMenu>
            </CDropdown>
          </CHeaderNav>
        </CHeader>

        <div className="px-4 py-2" style={{ background: '#fff', borderBottom: '1px solid #e8ecef' }}>
          <CBreadcrumb className="m-0">
            <CBreadcrumbItem>
              <Link to="/dashboard">Home</Link>
            </CBreadcrumbItem>
            <CBreadcrumbItem active>{currentPage}</CBreadcrumbItem>
          </CBreadcrumb>
        </div>

        <div className="app-body flex-grow-1 p-4">
          <CContainer fluid>
            <Outlet />
          </CContainer>
        </div>
      </div>
    </div>
  );
}
